import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { PixelPalette } from '@/lib/pixel'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { PixelSprite } from '@/components/pixel/pixel-sprite'
import { PixelArrow, pixelButtonClass } from '@/components/generals/pixel-button'

import SkateDino from '@/components/pixel/skate-dino'

import { BUDGETS, INTERESTS } from '@/constants/content'
import { ICON_CHECK } from '@/constants/sprites'

type FieldName = 'name' | 'email' | 'company' | 'budget' | 'services' | 'message'

type FieldErrors = Partial<Record<FieldName, string>>

type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    company: string
    budget: string
    services: Array<string>
    message: string
    website: string
}

interface FieldProps {
    id: string
    label: string
    error?: string
    optional?: boolean
    children: ReactNode
}

interface ContactResponse {
    ok?: boolean
    message?: string
    fields?: FieldErrors
}

const EMPTY_VALUES: FormValues = {
    name: '',
    email: '',
    company: '',
    budget: '',
    services: [],
    message: '',
    website: ''
}

const FIELD_ORDER: ReadonlyArray<FieldName> = ['name', 'email', 'company', 'budget', 'services', 'message']

const EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/

const MESSAGE_MAX = 4000

const CURRENT_COLOR: PixelPalette = { K: 'currentColor' }

const CHIP_CLASS = cn(
    'group inline-flex cursor-pointer select-none items-center gap-2 border-2 border-ink bg-white px-3.5 py-2 text-sm font-semibold',
    'transition-colors duration-150 ease-[steps(2,end)] hover:bg-bubblegum-soft',
    'has-[:checked]:bg-ink has-[:checked]:text-paper',
    'has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ink has-[:focus-visible]:outline-dashed'
)

const inputClass = (invalid: boolean): string => {
    return cn(
        'block w-full border-2 border-ink bg-white px-4 text-base text-ink placeholder:text-ink/40',
        'transition-shadow duration-150 ease-[steps(2,end)] focus:shadow-[4px_4px_0_0_var(--bubblegum)] focus-visible:outline-none',
        invalid && 'border-destructive shadow-[4px_4px_0_0_var(--destructive)]'
    )
}

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}

    if (values.name.trim().length < 2) errors.name = "Please tell us your name."
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = "Please enter a valid email address."
    if (values.message.trim().length < 10) errors.message = "Tell us a little more, at least 10 characters."

    return errors
}

const Field: FC<FieldProps> = ({
    id,
    label,
    error,
    optional = false,
    children
}): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='mb-2 flex items-baseline gap-2 text-xs font-bold tracking-wide uppercase'>
                {label}
                {optional && <span className='font-semibold tracking-normal text-muted-foreground normal-case'>(optional)</span>}
            </label>

            {children}

            {error && (
                <p id={`${id}-error`} className='mt-2 text-sm font-semibold text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(EMPTY_VALUES)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [notice, setNotice] = useState('')
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    const update = (field: 'name' | 'email' | 'company' | 'budget' | 'message' | 'website') => {
        return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { value } = event.target

            setValues(current => ({ ...current, [field]: value }))

            if (field !== 'website' && errors[field]) {
                setErrors(current => ({ ...current, [field]: undefined }))
            }
        }
    }

    const toggleService = (service: string) => {
        setValues(current => ({
            ...current,
            services: current.services.includes(service)
                ? current.services.filter(item => item !== service)
                : [...current.services, service]
        }))
    }

    const nudgeForm = () => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        formRef.current?.animate(
            [
                { transform: 'translateX(0)' },
                { transform: 'translateX(-8px)' },
                { transform: 'translateX(8px)' },
                { transform: 'translateX(-4px)' },
                { transform: 'translateX(0)' }
            ],
            { duration: 360, easing: 'steps(4, end)' }
        )
    }

    const focusFirstError = (found: FieldErrors) => {
        const first = FIELD_ORDER.find(field => found[field])

        if (!first) return

        formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setNotice('')
            nudgeForm()
            focusFirstError(found)
            return
        }

        setStatus('submitting')
        setNotice('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => null) as ContactResponse | null

            if (response.ok && data?.ok) {
                setStatus('success')
                setValues(EMPTY_VALUES)
                setErrors({})
                window.requestAnimationFrame(() => successRef.current?.focus())
                return
            }

            if (data?.fields) {
                setErrors(data.fields)
                focusFirstError(data.fields)
            }

            setStatus('error')
            setNotice(data?.message ?? "Something went wrong. Please try again in a moment.")
            nudgeForm()
        } catch {
            setStatus('error')
            setNotice("We could not reach the server. Check your connection and try again.")
            nudgeForm()
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='flex flex-col items-center py-6 text-center outline-none'
            >
                <SkateDino className='w-[210px] animate-ollie' />

                <h3 className='mt-8 font-heading text-4xl leading-none md:text-5xl'>
                    message received!
                </h3>

                <p className='mt-4 max-w-sm text-base leading-relaxed font-medium'>
                    Thanks for reaching out. We will get back to you within one business day with first ideas.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className={pixelButtonClass({ size: 'md', className: 'mt-8' })}
                >
                    send another
                </button>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form ref={formRef} noValidate onSubmit={onSubmit} className='relative space-y-6'>
            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='contact-website'>Leave this field empty</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            <div className='grid gap-6 sm:grid-cols-2'>
                <Field id='contact-name' label="your name" error={errors.name}>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        required
                        autoComplete='name'
                        maxLength={100}
                        placeholder="Alex Rivera"
                        value={values.name}
                        onChange={update('name')}
                        aria-invalid={errors.name ? true : undefined}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                        className={cn(inputClass(Boolean(errors.name)), 'h-12')}
                    />
                </Field>

                <Field id='contact-email' label="email" error={errors.email}>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        required
                        inputMode='email'
                        autoComplete='email'
                        maxLength={254}
                        placeholder="alex@company.com"
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={errors.email ? true : undefined}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                        className={cn(inputClass(Boolean(errors.email)), 'h-12')}
                    />
                </Field>
            </div>

            <Field id='contact-company' label="company" optional>
                <input
                    id='contact-company'
                    name='company'
                    type='text'
                    autoComplete='organization'
                    maxLength={120}
                    placeholder="Your brand or company"
                    value={values.company}
                    onChange={update('company')}
                    className={cn(inputClass(false), 'h-12')}
                />
            </Field>

            <fieldset>
                <legend className='mb-2 flex items-baseline gap-2 text-xs font-bold tracking-wide uppercase'>
                    budget
                    <span className='font-semibold tracking-normal text-muted-foreground normal-case'>(optional)</span>
                </legend>

                <div className='flex flex-wrap gap-2'>
                    {BUDGETS.map(budget => (
                        <label key={budget} className={CHIP_CLASS}>
                            <input
                                type='radio'
                                name='budget'
                                value={budget}
                                checked={values.budget === budget}
                                onChange={update('budget')}
                                className='sr-only'
                            />
                            {budget}
                        </label>
                    ))}
                </div>
            </fieldset>

            <fieldset>
                <legend className='mb-2 flex items-baseline gap-2 text-xs font-bold tracking-wide uppercase'>
                    what do you need?
                    <span className='font-semibold tracking-normal text-muted-foreground normal-case'>(optional)</span>
                </legend>

                <div className='flex flex-wrap gap-2'>
                    {INTERESTS.map(interest => (
                        <label key={interest} className={CHIP_CLASS}>
                            <input
                                type='checkbox'
                                name='services'
                                value={interest}
                                checked={values.services.includes(interest)}
                                onChange={() => toggleService(interest)}
                                className='sr-only'
                            />
                            <PixelSprite
                                rows={ICON_CHECK}
                                palette={CURRENT_COLOR}
                                className='hidden h-2.5 w-auto group-has-[:checked]:block'
                            />
                            {interest}
                        </label>
                    ))}
                </div>
            </fieldset>

            <Field id='contact-message' label="tell us about your project" error={errors.message}>
                <textarea
                    id='contact-message'
                    name='message'
                    required
                    rows={5}
                    maxLength={MESSAGE_MAX}
                    placeholder="What are you working on, and what would success look like?"
                    value={values.message}
                    onChange={update('message')}
                    aria-invalid={errors.message ? true : undefined}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    className={cn(inputClass(Boolean(errors.message)), 'min-h-36 resize-y py-3 leading-relaxed')}
                />

                <p aria-hidden='true' className='mt-1.5 text-right text-xs font-semibold text-muted-foreground tabular-nums'>
                    {values.message.length}/{MESSAGE_MAX}
                </p>
            </Field>

            {status === 'error' && notice && (
                <p role='alert' className='border-2 border-ink bg-[#ffe3e3] px-4 py-3 text-sm font-semibold'>
                    {notice}
                </p>
            )}

            <div className='flex flex-col-reverse gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-xs leading-relaxed font-medium text-muted-foreground'>
                    We only use your details to reply to you. No newsletters, no spam.
                </p>

                <button
                    type='submit'
                    disabled={submitting}
                    className={pixelButtonClass({ size: 'lg', className: 'w-full sm:w-auto' })}
                >
                    {submitting ? (
                        <>
                            sending
                            <span aria-hidden='true' className='flex gap-1'>
                                {[0, 1, 2].map(dot => (
                                    <span key={dot} className='size-2 animate-caret bg-ink' style={{ animationDelay: `${dot * 160}ms` }} />
                                ))}
                            </span>
                        </>
                    ) : (
                        <>
                            send it!
                            <PixelArrow />
                        </>
                    )}
                </button>
            </div>

            <p className='sr-only' role='status'>
                {submitting ? "Sending your message" : ''}
            </p>
        </form>
    )
}

export default ContactForm
